import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { Button } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import {
  Screen,
  NoParameters,
  ScreenTransitionManagerProvider,
} from "@jugl-web/utils/utils/ScreenTransitionManager";

import { TypeCheckboxFilter } from "./components/TypeCheckboxFilter";
import { TypeDateFilter } from "./components/TypeDateFilter";
import { TypeCustomerPicker } from "./components/TypeCustomerPicker";
import { useFilters } from "./hooks/useFilters";

const INITIAL_SCREEN: Screen<{
  entry: NoParameters;
}> = {
  name: "entry",
};

interface FiltersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: ReturnType<typeof useFilters>;
}

const FiltersDialogInner = ({
  isOpen,
  onClose,
  onApply,
  filters,
}: FiltersDialogProps) => {
  const { t } = useTranslations();
  const { fields, resetToCommitted, activeFiltersCount } = filters;

  const handleCancel = () => {
    resetToCommitted();
    onClose();
  };

  const handleApply = () => {
    filters.commitDraft();
    onApply();
  };

  return (
    <BottomCenteredDrawer
      isOpen={isOpen}
      onClose={handleCancel}
      header={{
        type: "title",
        title: t({
          id: "invoices-page.filters",
          defaultMessage: "Filters",
        }),
      }}
    >
      <BottomCenteredDrawer.Content>
        <div className="overflow-y-auto">
          {fields.map((field) => {
            if (field.type === "checkbox") {
              return (
                <TypeCheckboxFilter
                  key={field.name}
                  title={field.title}
                  options={field.options}
                  filters={filters}
                />
              );
            }

            if (field.type === "date") {
              return (
                <TypeDateFilter
                  key={field.name}
                  title={field.title}
                  filters={filters}
                />
              );
            }

            if (field.type === "customer") {
              return (
                <TypeCustomerPicker
                  key={field.name}
                  title={field.title}
                  filters={filters}
                />
              );
            }

            return null;
          })}
        </div>
      </BottomCenteredDrawer.Content>
      <BottomCenteredDrawer.Actions className="flex justify-center gap-3">
        <Button color="grey" className="h-10 w-[200px]" onClick={handleCancel}>
          {t({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="h-10 w-[200px]"
          onClick={handleApply}
        >
          {activeFiltersCount > 0
            ? t(
                {
                  id: "common.apply-with-count",
                  defaultMessage: "Apply {count}",
                },
                {
                  count: activeFiltersCount,
                }
              )
            : t({ id: "common.apply", defaultMessage: "Apply" })}
        </Button>
      </BottomCenteredDrawer.Actions>
    </BottomCenteredDrawer>
  );
};

export const FiltersDialog = (props: FiltersDialogProps) => (
  <ScreenTransitionManagerProvider initialScreen={INITIAL_SCREEN}>
    <FiltersDialogInner {...props} />
  </ScreenTransitionManagerProvider>
);
